import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReactToPrint } from 'react-to-print';
import QuotePrintTemplate from '../../../components/admin/quote-print-template/quote-print-template.component';
import { Quote } from '../../../models/quote.model';
import { getQuote } from '../../../services/quotes.service';
import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';

export default function QuotePreviewContainer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { quoteId } = useParams();
  const { isAuthenticated } = useAppContext();
  const stateQuote = (location.state as { quote?: Partial<Quote> } | null)?.quote;
  const [quote, setQuote] = useState<Partial<Quote> | undefined>(stateQuote);
  const [isLoading, setIsLoading] = useState(!stateQuote);
  const [error, setError] = useState('');
  const contentToPrint = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: contentToPrint });

  useEffect(() => {
    if (stateQuote || !isAuthenticated || !quoteId || quoteId === 'new') {
      setIsLoading(false);
      return;
    }
    getQuote(quoteId)
      .then(setQuote)
      .catch((err) => {
        onError(err);
        setError('Failed to load quote. Please try again.');
      })
      .finally(() => setIsLoading(false));
  }, [quoteId, isAuthenticated, stateQuote]);

  const backPath = quoteId && quoteId !== 'new' ? `/quotes/${quoteId}` : '/quotes/new';

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrint()} disabled={!quote}>
          Print
        </Button>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}>
          Back
        </Button>
      </Box>
      {quote && (
        <Paper sx={{ maxWidth: 900, mx: 'auto' }}>
          <div ref={contentToPrint}>
            <QuotePrintTemplate quoteData={quote} preview />
          </div>
        </Paper>
      )}
    </Box>
  );
}
